<template>
  <v-main
    class="service-section-overflow"
  >
    <div class="service-container">
      <v-overlay :value="overlay">
        <v-progress-circular
          indeterminate
          size="64"
        />
      </v-overlay>
      <v-card
        v-for="serviceItem in serviceItemsArray"
        :key="serviceItem.service_id"
        color="RMwhite"
        class="service-card"
        elevation="0"
      >
        <v-card-title
          :class="mobileBreakpoints() ? 'pa-0' : ''"
          class="service-title pa-3 mb-0 d-flex green-text text--accent-4 flex-nowrap"
          style="cursor: pointer;"
          @click="switchServiceSelected(`${serviceItem.policy_id}, ${serviceItem.service_id}, ${serviceItem.service_type}`, `${serviceItem.translationPath}`)"
        >
          <v-switch
            v-model="servicesSelected"
            style="pointer-events: none"
            dense
            inset
            hide-details
            :value="`${serviceItem.policy_id}, ${serviceItem.service_id}, ${serviceItem.service_type}`"
            color="RMprimary"
            :disabled="map.disableAnimationPlay || disableCloseService"
          />
          <span style="word-break: break-word; color: #505050;">
            {{ serviceItem.translationPath.replace(/_/g, ' ') }}
          </span>
          <v-spacer />
          <v-tooltip
            v-if="(serviceItem.info_app && $t(serviceItem.info_app) !== '') || (serviceItem.info_policy && $t(serviceItem.info_policy) !== '')"
            v-model="serviceItem.infoTooltip"
            top
            :max-width="!mobileBreakpoints() ? '400' :'200'"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                color="RMprimary"
                class="ml-3"
                v-bind="attrs"
                v-on="on"
                @mouseleave="serviceItem.infoTooltip = false"
                @click.stop="serviceItem.infoTooltip = !serviceItem.infoTooltip"
              >
                mdi-information-variant-circle-outline
              </v-icon>
            </template>
            <span style="white-space: pre-line;">
              {{ $t(serviceItem.info_app) }} {{ $t(serviceItem.info_policy) }}
            </span>
          </v-tooltip>
        </v-card-title>
        <service-card
          v-if="allWorkspaces.length > 0"
          :all-workspaces="allWorkspaces"
          :service-selected="servicesSelected"
          :service-card-id="serviceItem.service_id"
          @closeRasterService="closeRasterService($event)"
          @closeAllOtherServices="closeAllOtherServices($event)"
          @activatedNewArchiveService="$emit('activatedNewArchiveService')"
          @changedServiceLayer="$emit('changedServiceLayer')"
          @disable-close-service="disableCloseService = $event"
          @check-specific-default-service="checkSpecificDefaultService()"
          @updateAllWorkspaces="updateAllWorkspaces($event)"
        />
      </v-card>
    </div>
  </v-main>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { getFormattedDate } from '@/utils.js';

export default {
  components: {
    ServiceCard: () => import('@/components/layout/ServiceCard')
  },
  props: {
    specificDefaultService: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      specificDefaultServiceCounter: {},
      servicesSelected: [],
      allWorkspaces: [],
      serviceSelectedId: '',
      overlay: false,
      disableCloseService: false
    };
  },
  computed: {
    ...mapState(['policy', 'wsServices', 'map', 'drawer', 'app_user', 'table_chart', 'global_layers']),
    serviceItemsArray() {
      let serviceItemsArray = [];

      if (this.policy.enabledServicesPolicy != 0) {
        serviceItemsArray = [
          ...this.policy.enabledServicesPolicy.WS.map(e => ({
            translationPath: this.$t(`ServiceCard.${e.service_id}.title`),
            translationPathDescription: `ServiceCard.${e.service_id}.description`,
            app_user_id: e.app_user_id,
            service_id: e.service_id,
            policy_id: e.policy_id,
            service_type: e.service_type,
            info_app: e.info_app,
            info_policy: e.info_policy,
            infoTooltip: false
          }))
        ];
        this.policy.enabledServicesPolicy.WS.forEach(e => this.map.renderOpacityMenu[e.service_id] = true);
      }

      serviceItemsArray.sort(((a, b) => (a.translationPath.localeCompare(b.translationPath, 'en', { numeric: true }))));

      return serviceItemsArray;
    }
  },
  watch: {
    $route() {},
    '$route.params': {
      async handler(newValue) {
        if (newValue) {
          if (newValue.id) {
            this.allWorkspaces = [];
            this.servicesSelected = [];
            this.map.rasterMarkerLabel = '';
            this.map.rasterMarkerUdm = '';
            this.map.rasterMarkerValue = '';
            this.map.rasterMarkerRows = [];
            window.clearInterval(this.map.animationInterval);
            this.map.meteogramActive = false;
            this.map.animationInterval = null;
            this.map.disableAnimationPlay = false;
            this.disableCloseService = false;
            const scope = this.$route.params.id.replace('-', '');
            this.specificDefaultServiceCounter[scope] = 0;

            for await (const layer of this.map.OLmap.getLayers().getArray().slice(1)) {
              await this.removeLayerByServiceID(layer.values_.service_id);
            }

            this.table_chart.completeVectorChartItems = [];
            this.table_chart.layersChartColors = {};
            this.map.aggregationIntervals = {};
            this.closePopupOnMap();
          }
        }
      },
      immediate: true
    },
    specificDefaultService: {
      async handler(newValue) {
        if (newValue) {
          this.defaultServiceWS(this.$route.params.id.replace('-', ''));
        }
      }
    }
  },
  mounted() {
    this.defaultServiceWS('GLOBAL');
  },
  methods: {
    ...mapActions(['removeActiveService', 'storeWsServices', 'removeLayerByServiceID', 'resetAnimationTime', 'setGlobalLayer', 'closePopupOnMap']),
    isLandscape() {
      return /Mobi|Android/i.test(navigator.userAgent) && window.innerHeight < window.innerWidth;
    },
    mobileBreakpoints() {
      return /Mobi|Android/i.test(navigator.userAgent) || this.isLandscape();
    },
    async switchServiceSelected(workspaceToSwitch, translationPath) {
      if (this.map.disableAnimationPlay || this.disableCloseService) {
        return;
      }

      if (this.servicesSelected.includes(workspaceToSwitch)) {
        this.servicesSelected.splice(this.servicesSelected.indexOf(workspaceToSwitch), 1);
        await this.removeLayerByServiceID(workspaceToSwitch.split(', ')[1]);
        await this.removeActiveService(workspaceToSwitch.split(', ')[1]);
        this.reloadLegend();
      } else {
        this.servicesSelected.push(workspaceToSwitch);
      }

      this.changeWorkspaceLayer(workspaceToSwitch);
      this.googleTag(translationPath, workspaceToSwitch);
    },
    async changeWorkspaceLayer(selectedWs) {
      const index = this.servicesSelected.indexOf(selectedWs);
      const workspace = this.servicesSelected[index] || '';
      const [policy_id, service_id, service_type] = workspace.split(', ') || '';

      if (index !== -1) {
        this.map.renderOpacityMenu[service_id] = false;
        this.$nextTick(() => {
          this.map.renderOpacityMenu[service_id] = true;
        });
      }

      if (workspace !== '') {
        this.overlay = true;
        this.serviceSelectedId = service_id;

        await this.storeWsServices(
          {
            policy_id: policy_id,
            service_id: service_id,
            service_type: service_type
          }
        );

        const thisWScolors = {};
        const thisWSaggregationLayersInterval = [];
        this.wsServices.wsAppUserPolicy.forEach(workspace => {
          this.allWorkspaces.push(workspace);
          workspace.ws_policy.forEach(item => {
            thisWScolors[item.layer_label.split(',')[1].split('_serviceID:')[0]] = item.layer_color;

            if (item.aggregation_interval) {
              thisWSaggregationLayersInterval.push(`interval_${item.layer_name}:${item.aggregation_interval}`);
            }
          });
        });
        this.table_chart.layersChartColors[service_id] = thisWScolors;

        if (thisWSaggregationLayersInterval.length) {
          this.map.aggregationIntervals[service_id] = thisWSaggregationLayersInterval.join(';');
        }

        this.overlay = false;
      } else {
        const service_id = selectedWs.split(', ')[1];
        delete this.table_chart.layersChartColors[service_id];
        delete this.map.aggregationIntervals[service_id];
        const updatedWorkspaceArray = this.allWorkspaces.filter(workspace => workspace.service_id !== service_id);
        this.allWorkspaces = updatedWorkspaceArray;

        if (this.$route.params.id === 'Forecast' || this.$route.params.id === 'Real-Time') {
          this.map.isRefreshingDataServiceID = this.map.isRefreshingDataServiceID.filter(id => id !== service_id);

          if (Object.keys(this.map.refreshIntervalID).includes(service_id)) {
            clearInterval(this.map.refreshIntervalID[service_id]);
            delete this.map.refreshIntervalID[service_id];
          }

          if (this.map.isAnimating.includes(service_id)) {
            const indexToRemove = this.map.isAnimating.indexOf(service_id);
            this.map.isAnimating.slice(indexToRemove, 1);
          }
        }

        if (this.app_user.defaultService.GLOBAL?.service_details && this.app_user.defaultService.GLOBAL?.service_details.length > 0) {
          const sliceIndex = this.app_user.defaultService.GLOBAL.service_details.findIndex(item => item.service_id === service_id);

          if (sliceIndex > -1) {
            if (this.map.layerToSelectVector[this.app_user.defaultService.GLOBAL.service_details[sliceIndex].service_id]) {
              this.map.layerToSelectVector[this.app_user.defaultService.GLOBAL.service_details[sliceIndex].service_id] = {};
            }

            if (this.map.layerToSelectRaster[this.app_user.defaultService.GLOBAL.service_details[sliceIndex].service_id]) {
              this.map.layerToSelectRaster[this.app_user.defaultService.GLOBAL.service_details[sliceIndex].service_id] = {};
            }

            this.app_user.defaultService.GLOBAL.service_details.splice(sliceIndex, 1);
          }
        }

        this.map.rasterMarkerLabel = '';
        this.map.rasterMarkerUdm = '';
        this.map.rasterMarkerValue = '';
        this.map.rasterMarkerRows = [];

        for await (const layer of this.map.OLmap.getLayers().getArray()) {
          if (layer.values_.service_id === service_id) {
            await this.resetAnimationTime();
            await this.removeLayerByServiceID(service_id);
          }
        }
      }
    },
    closeRasterService(service_id) {
      // Filtra servicesSelected
      const serviceToClose = this.servicesSelected.filter(s => {
        const s_id = s.split(', ')[1];
        const workspace = this.allWorkspaces.find(w => w.service_id === s_id);

        return s_id !== service_id && workspace && workspace.ws_policy?.[0]?.layer_type === 'RASTER';
      });

      serviceToClose.forEach(s => {
        this.switchServiceSelected(s);
        this.removeActiveService(s.split(', ')[1]);
      });

      // Filtra allWorkspaces: tieni solo il raster specifico e i vettoriali
      this.allWorkspaces = this.allWorkspaces.filter(w => w.service_id === service_id || w.ws_policy?.[0]?.layer_type === 'VECTOR');
    },
    closeAllOtherServices(service_id) {
      this.allWorkspaces = this.allWorkspaces.filter(ws => ws.service_id === service_id);
      const serviceToClose = this.servicesSelected.filter(ws => ws.split(', ')[1] !== service_id);

      serviceToClose.forEach(s => {
        this.switchServiceSelected(s);
        this.removeActiveService(s.split(', ')[1]);
      });
    },
    async defaultServiceWS(scope) {
      if (this.app_user.defaultService[scope]?.service_type === 'WS') {
        const processServiceById = async (service_id) => {
          const serviceItem = this.serviceItemsArray.find(ws => ws.service_id === service_id);

          if (serviceItem) {
            const selectedWS = `${serviceItem.policy_id}, ${serviceItem.service_id}, ${serviceItem.service_type}`;
            this.servicesSelected.push(selectedWS);
            await this.changeWorkspaceLayer(selectedWS);
          }
        };

        const serviceDetails = this.app_user.defaultService[scope]?.service_details || [];

        if (this.mobileBreakpoints()) {
          const mobileService = serviceDetails.find(s => s.layer_type === 'RASTER') || serviceDetails[0];

          if (mobileService) {
            this.app_user.defaultService[scope].service_details = [];
            this.app_user.defaultService[scope].service_details.push(mobileService);

            await processServiceById(mobileService.service_id);
          }

          this.$emit('automaticlyCloseServiceMenu');
        } else {
          await Promise.all(serviceDetails.map(item => processServiceById(item.service_id)));
        }

        this.app_user.defaultService[scope].global_layer_id.forEach(item => {
          const gLayer = this.global_layers.globalLayers.find(layer => layer.global_layer_id === item.id);
          this.global_layers.selectedGlobalLayer.push(gLayer);
          this.setGlobalLayer([gLayer, 'GLOBAL LAYER']);
        });

        this.app_user.defaultService[scope].customer_layer_id.forEach(item => {
          const cLayer = this.global_layers.customerLayers.find(layer => layer.global_layer_id === item.id);
          this.global_layers.selectedGlobalLayer.push(cLayer);
          this.setGlobalLayer([cLayer, 'CUSTOMER LAYER']);
        });
      } else {
        setTimeout(() => {
          this.app_user.defaultServiceGL.forEach(item => {
            const gLayer = this.global_layers.globalLayers.find(layer => layer.global_layer_id === item.id);
            this.global_layers.selectedGlobalLayer.push(gLayer);
            this.setGlobalLayer([gLayer, 'GLOBAL LAYER']);
          });

          this.app_user.defaultServiceCL.forEach(item => {
            const cLayer = this.global_layers.customerLayers.find(layer => layer.global_layer_id === item.id);
            this.global_layers.selectedGlobalLayer.push(cLayer);
            this.setGlobalLayer([cLayer, 'CUSTOMER LAYER']);
          });
        }, 500);
      }
    },
    googleTag(serviceName, selectedWs) {
      if (this.servicesSelected.indexOf(selectedWs) !== -1) {
        this.$gtag.event('Servizio', {
          event_category: `${this.$route.params.id == undefined ? 'Home' : this.$route.params.id}`,
          event_label: `${Date.now()} - ${this.app_user.username} - ${this.app_user.crm_id} - ${getFormattedDate(new Date(), true, true)}`,
          value: serviceName
        });
      }
    },
    checkSpecificDefaultService() {
      const scope = this.$route.params.id.replace('-', '');
      this.specificDefaultServiceCounter[scope]++;
      const countOperations = this.app_user.defaultService[scope]?.service_details.length + this.app_user.defaultService[scope]?.service_details.filter(e => e.is_meteogram).length;

      if (this.specificDefaultServiceCounter[scope] > countOperations) {
        this.map.specificDefaultService[scope] = false;
      }
    },
    getRouteIdTranslation(id) {
      switch (id) {
        case 'Archive':
          return 'archive';
        case 'Forecast':
          return 'prevision';
        default:
          return 'real_time';
      }
    },
    reloadLegend() {
      this.map.reloadLegend = true;
      this.$nextTick(() => {
        this.map.reloadLegend = false;
      });
    },
    updateAllWorkspaces(updatedWorkspace) {
      const index = this.allWorkspaces.findIndex(ws => ws.ws_id === updatedWorkspace.ws_id);

      if (index !== -1) {
        this.$set(this.allWorkspaces, index, updatedWorkspace);
      }
    }
  }
};
</script>

<style scoped>
.v-text-field >>> label {
  font-size: 0.8em;
}
.input__label {
  color: grey;
}
.no-uppercase {
  text-transform: none;
  color: white !important;
}
.title-color{
  color: white !important;
}
.service-section-overflow{
  overflow-y: auto;
  height: 100% !important;
}
/* change default table css with custom  */
.v-data-table /deep/ .v-data-table__wrapper {
  overflow: unset !important;
}
.green--text {
  line-height: 1.1rem !important;
  font-size: 1.2rem !important
}
.service-container {
  margin-right: 4px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 100%;
}
.service-card {
  border-radius: 16px!important;
}

.v-input--selection-controls {
  margin-top: 0px!important;
  padding-top: 0px!important;
}

::-webkit-scrollbar {
  border-radius: 12px;
  background-color: #fff;
}

::-webkit-scrollbar-thumb {
  border-radius: 12px;
  background-color: rgb(0, 166, 68, 1);
}

.service-title {
  min-height: 48px;
}

@media (max-device-width:599px), (max-device-height:599px) and (orientation: landscape){
  .green--text {
    font-size: 0.8rem !important
  }

  .service-section-overflow {
    display: flex;
    flex-direction: column;
    background-color: #7ad49c;
  }

  .go-back-arrow-div {
    padding: 12px;
    display: flex;
    justify-content: right;
    align-items: center;
    position: fixed;
    width: 75%;
    height: 60px;
    z-index: 5;
    background-color: #00a644;
  }

  .service-container {
    padding: 8px;
    background-color: #7ad49c;
    margin: 0px!important;
  }
}
</style>
